import { memo, useEffect, useRef } from 'react'

import { useQuery } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Editor, EditorInstance } from '@shared/components/Editor'
import { LoadingIndicator, NotFound } from '@shared/layouts'
import { useVideoObjectUpdateMutation } from 'hooks/mutations/content/VideoObject'
import { videoObjectConfigRoute } from 'pages/NWPlatform/Workspaces/Workspace/Video/Objects/Object'
import { videoObjectQueryKeys } from 'services/queryKeys'

const JSONRaw = () => {
  const { t } = useTranslation(['common', 'domain'])
  const { enqueueSnackbar } = useSnackbar()

  const editorInstanceRef = useRef<EditorInstance>(undefined)

  const { videoobjectId: objectId } = videoObjectConfigRoute.useParams()

  const videoObjectQuery = useQuery(videoObjectQueryKeys.detail({ videoId: objectId }))
  const { data: videoObject } = videoObjectQuery

  const { mutate } = useVideoObjectUpdateMutation()

  useEffect(() => {
    if (editorInstanceRef.current && videoObjectQuery.isSuccess) {
      editorInstanceRef.current?.getModel()?.setValue(window.JSON.stringify(videoObject, null, 4))
    }
  }, [videoObjectQuery.isSuccess, videoObject])

  if (videoObjectQuery.isLoading) {
    return <LoadingIndicator />
  }

  if (!videoObject) {
    return <NotFound />
  }

  if (videoObject.streamId) {
    return <Editor value={videoObject} readOnly />
  }

  return (
    <Editor
      value={videoObject}
      onSubmit={(data) => {
        mutate(
          {
            videoId: objectId,
            videoObjectEdit: data,
          },
          {
            onSuccess: () => {
              enqueueSnackbar(t('domain:Workspace.Video.videoObjectSaved'), { variant: 'success' })
            },
          },
        )
      }}
      getEditorInstance={(editorInstance: EditorInstance) => (editorInstanceRef.current = editorInstance)}
    />
  )
}

export const JSON = memo(JSONRaw)
